/*
  Что делают с приказом после суда: отмечают события и дописывают суд - §3.6.

  🔴 С 22.09.2026 это ОКНО, а не блок внутри карточки документа (решение
  владельца вместе с табличным разделом). Причина: раздел стал таблицей, а
  форма из трёх полей внутри строки таблицы либо расталкивает её, либо не
  помещается. В окне человек видит ровно то, что нужно сейчас, а таблица не
  меняет высоту.

  Поля появляются по виду события, а не висят все сразу: у удержания есть
  сумма, у предъявления - куда предъявлен, у остальных ни того, ни другого.
  Форма из пяти полей, три из которых всегда пусты, читается как неисправная.

  Правила чек-листа формы соблюдаются здесь, а не в вызывающем экране: ошибка
  живёт ВНУТРИ окна (иначе за окном её не видно, и человек считает, что ничего
  не произошло), успех закрывает окно и обновляет список, закрытие сбрасывает
  состояние - окно каждый раз создаётся заново.
*/

import { useState } from 'react'

import { api } from '../api/client.js'
import { CHANNEL_TITLES, RECORDABLE_ENFORCEMENT_EVENTS, money } from '../api/format.js'
import { Field, Notice } from './Common.jsx'
import CourtField from './CourtField.jsx'
import Modal from './Modal.jsx'
import Dropdown from './Select.jsx'

const NEEDS_AMOUNT = 'partial_payment'
const NEEDS_CHANNEL = 'presented'

export default function EnforcementActions({ item, sections, sectionsError, onDone, onClose }) {
  const [kind, setKind] = useState('')
  const [happenedOn, setHappenedOn] = useState('')
  const [amount, setAmount] = useState('')
  const [channel, setChannel] = useState('')
  const [court, setCourt] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)

  const ready =
    kind &&
    happenedOn &&
    (kind !== NEEDS_AMOUNT || Number(amount) > 0) &&
    (kind !== NEEDS_CHANNEL || channel)

  async function record(event) {
    event.preventDefault()
    setBusy(true)
    setError(null)
    try {
      const updated = await api.recordEnforcementEvent(item.id, {
        kind,
        happened_on: happenedOn,
        amount: kind === NEEDS_AMOUNT ? amount : null,
        channel: kind === NEEDS_CHANNEL ? channel : null,
      })
      onDone(`Отмечено: ${updated.state_title}. Срок пересчитан.`, 'ok')
    } catch (failure) {
      setError(failure)
    } finally {
      setBusy(false)
    }
  }

  /*
    Суд дописывается только у приказов, заведённых до появления колонки
    09.09.2026. У остальных он задан при заведении, и второе поле для того же
    значения превратило бы окно в форму редактирования.
  */
  async function saveCourt(event) {
    event.preventDefault()
    setBusy(true)
    setError(null)
    try {
      await api.setEnforcementCourt(item.id, court.trim())
      onDone('Суд указан. Теперь по этому приказу собирается заявление об индексации.', 'ok')
    } catch (failure) {
      setError(failure)
    } finally {
      setBusy(false)
    }
  }

  const labelId = `event-kind-${item.id}`
  const channelId = `event-channel-${item.id}`
  const note = `${money(item.awarded_amount)} присуждено${
    item.account_number ? ` · счёт № ${item.account_number}` : ''
  }`

  return (
    <Modal title={`Дело № ${item.case_number}`} note={note} onClose={onClose} busy={busy}>
      {item.court_name ? null : (
        <form onSubmit={saveCourt}>
          <div className="row">
            {/*
              Участок выбирается из заведённых, но не подставляется сам: это
              факт из полученного приказа. Подставленный факт человек
              подтвердил бы не читая, а заявление об индексации ушло бы не в
              тот суд (14.09.2026).
            */}
            <CourtField
              label="Суд, вынесший приказ"
              value={court}
              onChange={setCourt}
              sections={sections}
              error={sectionsError}
              placeholder="выберите из списка или впишите свой"
              required
            />
            <button className="button button--quiet" type="submit" disabled={busy || !court.trim()}>
              Сохранить суд
            </button>
          </div>
          <div className="muted">Без суда заявление об индексации не собрать.</div>
        </form>
      )}

      <form onSubmit={record}>
        <div className="row">
          <div className="field field--m">
            <span className="field__label" id={labelId}>
              Что произошло
            </span>
            <Dropdown
              value={kind}
              onChange={setKind}
              options={RECORDABLE_ENFORCEMENT_EVENTS}
              empty="выберите"
              labelledBy={labelId}
            />
          </div>
          <Field label="Когда" width="s">
            <input
              className="field__input"
              type="date"
              value={happenedOn}
              onChange={(e) => setHappenedOn(e.target.value)}
              required
            />
          </Field>
          {kind === NEEDS_AMOUNT ? (
            <Field label="Сумма удержания, ₽" width="s">
              <input
                className="field__input"
                type="number"
                step="0.01"
                min="0.01"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                required
              />
            </Field>
          ) : null}
          {kind === NEEDS_CHANNEL ? (
            <div className="field field--s">
              <span className="field__label" id={channelId}>
                Куда предъявлен
              </span>
              <Dropdown
                value={channel}
                onChange={setChannel}
                options={CHANNEL_TITLES}
                empty="выберите"
                labelledBy={channelId}
              />
            </div>
          ) : null}
        </div>
        <Notice error={error} />
        <div className="row row--tight">
          <button className="button" type="submit" disabled={busy || !ready}>
            Отметить
          </button>
          <button className="button button--quiet" type="button" onClick={onClose} disabled={busy}>
            Закрыть
          </button>
        </div>
      </form>
    </Modal>
  )
}
